import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { useGlobalFileUpload } from './useGlobalFileUpload'

/**
 * Hook to handle upload persistence and stale upload cancellation
 *
 * This hook:
 * - Checks for stale uploads on mount
 * - Checks for stale uploads when tab becomes visible
 * - Cleans up on unmount
 */
export const useUploadPersistence = () => {
  const queryClient = useQueryClient()
  const { checkAndCancelStaleUploads } = useGlobalFileUpload()
  const hasCheckedRef = useRef(false)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    // Prevent duplicate checks in React Strict Mode
    if (hasCheckedRef.current) return
    hasCheckedRef.current = true

    const performCheck = async (markReady = false) => {
      try {
        await checkAndCancelStaleUploads(queryClient)
      } catch (error) {
        console.error('❌ [UploadPersistence] Failed to check stale uploads:', error)
      } finally {
        if (markReady) {
          setIsReady(true)
        }
      }
    }

    // Check on mount
    void performCheck(true)

    // Check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void performCheck()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [queryClient, checkAndCancelStaleUploads])

  return isReady
}
